import { isNil } from 'lodash';
import type {
  DetailedProxyMetadata,
  FactorLensWithReturns,
  Fund,
  HistoricalSubjectRangeAnalysisErrorEnum,
  Portfolio,
  SimpleFund,
  SubjectRangeAnalysis,
  Lot,
} from 'venn-api';
import { getAppTitle, rebrandDataSource } from 'venn-ui-kit';
import {
  type AnalysisSubject,
  assertExhaustive,
  assertNotNil,
  flattenNode,
  flattenNodeWithStrategyPath,
  getAnalysisLabels,
  IS_PROD,
  isCashNode,
  isOptionNode,
  isStrategyNode,
  logExceptionIntoSentry,
  lowestFrequencyOf,
  type WithStrategies,
} from 'venn-utils';
import {
  type LayeredDetailedProxyMetadata,
  type BulkPickerDetailedProxyMetadata,
  getFirstProxy,
  getSecondProxy,
} from '../hooks/useProxyDataQuery';
import {
  type Allocation,
  type BulkManageCashRow,
  type BulkManageFactorRow,
  type BulkManageFundRow,
  type BulkManageLotRow,
  type BulkManageOptionRow,
  type BulkManagePortfolioRow,
  type BulkManageRow,
  type BulkManageStrategyRow,
  type CommonRangeData,
  type CreateUpdateMetadata,
  type HistoricalAllocation,
  type Timestamp,
} from './types';
import type { FundToBulkProxy } from '../modals/pickers/types';
import { isBulkManageFundRow, isProFormaBulkManageInvestmentRow } from './utils';
import {
  getHistoricalPortfolioDateTotalAllocations,
  getProFormaPortfolioTotalNetAssets,
  getLotsTotalQuantity,
} from './columns/utils';

const getHistoricalError = (
  error?: HistoricalSubjectRangeAnalysisErrorEnum,
): HistoricalAllocation['historicalError'] => {
  if (isNil(error)) {
    return undefined;
  }
  switch (error) {
    case 'EARLIEST_ALLOCATION_DATE_PREDATES_SUBJECT_START_DATE':
    case 'ALLOCATION_DATE_OUTSIDE_SUBJECT_RANGE':
    case 'INVESTMENT_RETURNS_NOT_AVAILABLE':
    case 'INVESTMENT_ALLOCATIONS_NOT_AVAILABLE':
      return {
        _type: 'error' as const,
        value: error,
      };
    case 'LATEST_ALLOCATION_DATE_EXCEEDS_SUBJECT_RETURNS_END_DATE':
      return {
        _type: 'warning' as const,
        value: error,
      };
    default:
      return assertExhaustive(error);
  }
};

const getPinnedRowData = (
  subject: AnalysisSubject,
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>,
  holdingsProxyDataByFund: Record<string, DetailedProxyMetadata | undefined>,
  benchmarkRange: SubjectRangeAnalysis | undefined,
  benchmark: Portfolio | Fund | null | undefined,
  commonRangeData: CommonRangeData,
  primaryFactorLens: FactorLensWithReturns | undefined,
) => {
  const pinnedRowData: BulkManageRow[] = [];

  pinnedRowData.push({
    rowType: 'axis',
    commonRangeData,
    fullPath: ['axis'],
    name: '',
    rowId: 'axis',
    axis: true,
  });

  if (benchmark) {
    const labels = getAnalysisLabels(subject.type, subject.secondaryLabel, subject.secondaryPortfolio?.updated);
    const name = benchmark.name ? `${labels.benchmark}: ${benchmark.name}` : labels.benchmark;
    const row = {
      ...('children' in benchmark
        ? mapPortfolioToRow(commonRangeData, name, benchmark, benchmarkRange)
        : mapFundToRow(
            commonRangeData,
            name,
            [],
            benchmark,
            getFirstProxy(proxyDataByFund[benchmark.id]),
            getSecondProxy(proxyDataByFund[benchmark.id]),
            holdingsProxyDataByFund[benchmark.id],
            benchmarkRange,
            true,
          )),
      secondary: true,
      isBenchmark: true,
    } satisfies BulkManageFundRow | BulkManagePortfolioRow;
    pinnedRowData.push(row);
  }

  if (primaryFactorLens) {
    const rowId = `Factors${primaryFactorLens.latestStartDate}${primaryFactorLens.earliestEndDate}`;
    const row: BulkManageFactorRow = {
      rowType: 'factor' as const,
      commonRangeData,
      name: 'Factors',
      rowId,
      fullPath: [rowId],
      startDate: primaryFactorLens.latestStartDate,
      endDate: primaryFactorLens.earliestEndDate,
      frequency: primaryFactorLens.maximumFrequency,
      secondary: true,
      dataSource: getAppTitle(),
    };
    pinnedRowData.push(row);
  }

  return pinnedRowData;
};

export const getBulkManagementData = (
  subject: AnalysisSubject,
  funds: SimpleFund[],
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>,
  holdingsProxyDataByFund: Record<string, DetailedProxyMetadata | undefined>,
  rangeDataByFund: Record<string, SubjectRangeAnalysis[]>,
  benchmarkRange: SubjectRangeAnalysis | undefined,
  benchmark: Portfolio | Fund | null | undefined,
  commonRangeData: CommonRangeData,
  primaryFactorLens: FactorLensWithReturns | undefined,
  cashRateRange: SubjectRangeAnalysis | undefined,
  referenceRateRange: SubjectRangeAnalysis | undefined,
  allocationDates: number[],
  showTotalRow: boolean,
  showLots: boolean = false,
): { rowData: BulkManageRow[]; pinnedRowData: BulkManageRow[] } => {
  const rowData: BulkManageRow[] = [];
  const fundIdToFund = new Map(funds?.map((f) => [f.id, f]));
  const flattenedPortfolioWithStrategyPaths = flattenNodeWithStrategyPath(subject.portfolio);
  const getNextFundIndex = (
    (fundToIndexMap: Map<string, number>) =>
    (id: string): number => {
      if (id in fundToIndexMap) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        fundToIndexMap[id] += 1;
      } else {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        fundToIndexMap[id] = 0;
      }
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      return fundToIndexMap[id];
    }
  )(new Map<string, number>());

  if (subject.fund) {
    rowData.push(
      mapFundToRow(
        commonRangeData,
        subject.fund.name,
        [],
        subject.fund,
        getFirstProxy(proxyDataByFund[subject.fund.id]),
        getSecondProxy(proxyDataByFund[subject.fund.id]),
        holdingsProxyDataByFund[subject.fund.id],
        rangeDataByFund[subject.fund.id]?.[getNextFundIndex(subject.fund.id)],
        false,
      ),
    );
  }

  const portfolioFundEntryMap = new Map<string, BulkManageRow>();
  for (const portfolioEntry of flattenedPortfolioWithStrategyPaths) {
    if (isStrategyNode(portfolioEntry)) {
      // If the node doesn't have a fund, it is either the root or a strategy.
      if (portfolioEntry.id !== subject.portfolio?.id && portfolioEntry.children.length) {
        rowData.push(mapStrategyToRow(commonRangeData, portfolioEntry));
      }
      continue;
    }

    if (isCashNode(portfolioEntry)) {
      rowData.push(
        mapCashToRow({
          commonRangeData,
          rangeAnalysis: (portfolioEntry.cashBalance ?? 0) >= 0 ? cashRateRange : referenceRateRange,
          cashNode: portfolioEntry,
        }),
      );
      continue;
    }

    const fundId = assertNotNil(portfolioEntry.fund).id;
    const fund = fundIdToFund.get(fundId);
    if (!fund) {
      // hasn't loaded yet, so skip it for now
      continue;
    }

    const index = getNextFundIndex(fundId);

    let newEntry: BulkManageFundRow | BulkManageOptionRow | BulkManageLotRow;

    if (isOptionNode(portfolioEntry)) {
      newEntry = mapOptionToRow({
        commonRangeData,
        rangeAnalysis: rangeDataByFund[fund.id]?.[index],
        optionNode: portfolioEntry,
      });
    } else {
      newEntry = mapFundToRow(
        commonRangeData,
        fund.name,
        portfolioEntry.strategyIdPath,
        fund,
        getFirstProxy(proxyDataByFund[fund.id]),
        getSecondProxy(proxyDataByFund[fund.id]),
        holdingsProxyDataByFund[fund.id],
        rangeDataByFund[fund.id]?.[index],
        false,
        portfolioEntry,
      );
    }

    const existingEntry = portfolioFundEntryMap.get(newEntry.rowId);
    if (existingEntry && isProFormaBulkManageInvestmentRow(existingEntry)) {
      // If we already have an entry with the the same exact rowId, we should just merge the allocations.
      // This is a rare edge case that can happen if somehow someone adds the same investment twice to a strategy.
      if (
        !isNil(existingEntry.allocation) ||
        (isProFormaBulkManageInvestmentRow(newEntry) && !isNil(newEntry.allocation))
      ) {
        existingEntry.allocation = (existingEntry.allocation ?? 0) + (portfolioEntry.allocation ?? 0);
      }
      continue;
    }
    portfolioFundEntryMap.set(newEntry.rowId, newEntry);
    rowData.push(newEntry);

    if (
      showLots &&
      isBulkManageFundRow(newEntry) &&
      portfolioEntry.fund &&
      portfolioEntry.lots &&
      portfolioEntry.lots.length > 0
    ) {
      const totalLotQuantity = portfolioEntry.lots.reduce((sum, lot) => sum + (lot.quantity ?? 0), 0);
      portfolioEntry.lots.forEach((lot, lotIndex) => {
        const lotRow = mapLotToRow(commonRangeData, lot, lotIndex, newEntry, fund, totalLotQuantity);
        rowData.push(lotRow);
      });
    }
  }

  let pinnedRowData = getPinnedRowData(
    subject,
    proxyDataByFund,
    holdingsProxyDataByFund,
    benchmarkRange,
    benchmark,
    commonRangeData,
    primaryFactorLens,
  );

  if (showTotalRow) {
    if (subject?.portfolio?.historical) {
      const portfolioDateTotalAllocations = getHistoricalPortfolioDateTotalAllocations(
        subject.portfolio,
        allocationDates,
      );
      pinnedRowData = [
        {
          rowType: 'total' as const,
          allocation: portfolioDateTotalAllocations,
          allocationType: 'historical',
          name: 'Total',
          fullPath: ['total-ce15479b-b395-4066-b6cb-236b1cbbc238'],
          rowId: 'total-ce15479b-b395-4066-b6cb-236b1cbbc238',
          secondary: true,
          historicalStartDate: undefined,
          historicalEndDate: undefined,
          historicalError: undefined,
        },
      ];
    } else {
      const proFormaTotalAllocations = getProFormaPortfolioTotalNetAssets(subject.portfolio);
      const proFormaTotalQuantity = getLotsTotalQuantity(subject.portfolio);
      pinnedRowData = [
        {
          rowType: 'total' as const,
          allocation: proFormaTotalAllocations,
          allocationType: 'proforma',
          quantity: proFormaTotalQuantity,
          name: 'Total',
          fullPath: ['total-ce15479b-b395-4066-b6cb-236b1cbbc238'],
          rowId: 'total-ce15479b-b395-4066-b6cb-236b1cbbc238',
          secondary: true,
        },
      ];
    }
  }

  try {
    // verify that all results have unique rowIds and paths
    const rowIds = new Set<string>();
    const paths = new Set<string>();
    for (const row of rowData) {
      if (rowIds.has(row.rowId)) {
        throw new Error(`Duplicate rowId: ${row.rowId} in ${subject.name}`);
      }
      rowIds.add(row.rowId);

      const path = row.fullPath.join('>');
      if (paths.has(path)) {
        throw new Error(`Duplicate path: ${path} in ${subject.name}}`);
      }
      paths.add(path);
    }
  } catch (e) {
    if (IS_PROD) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(e);
    } else {
      throw e;
    }
  }

  return { rowData, pinnedRowData };
};

const getCreateUpdateMetadata = (subject?: CreateUpdateMetadata): CreateUpdateMetadata => ({
  created: subject?.created,
  updated: subject?.updated,
  owner: subject?.owner,
  updatedBy: subject?.updatedBy,
});

const allocationTsToRecord = (allocationTs: Portfolio['closingAllocationsTs']): Record<Timestamp, Allocation> => {
  const record: Record<Timestamp, Allocation> = {};
  if (!allocationTs) {
    return record;
  }
  for (const allocation of allocationTs) {
    record[allocation[0]!] = allocation[1]!;
  }
  return record;
};

const mapStrategyToRow = (
  commonRangeData: CommonRangeData,
  strategy: WithStrategies<Portfolio>,
): BulkManageStrategyRow => {
  const strategyId = String(strategy.id);
  const fullPath = strategy.strategyIdPath.map(String);
  fullPath.push(String(strategy.id));
  const commonFields = {
    rowType: 'investment' as const,
    investmentType: 'strategy' as const,
    name: strategy.name,
    rowId: strategyId,
    fullPath,
    startDate: strategy.periodStart,
    endDate: strategy.periodEnd,
    frequency: strategy.lowestFrequency,
    commonRangeData,
  };
  if (strategy.historical) {
    return {
      ...commonFields,
      allocationType: 'historical' as const,
      allocation: allocationTsToRecord(strategy.closingAllocationsTs),
      historicalStartDate: undefined,
      historicalEndDate: undefined,
      historicalError: undefined,
    };
  }
  return {
    ...commonFields,
    allocationType: 'proforma' as const,
    allocation: strategy.netAssets,
  };
};

// exported to support testing only
export const mapPortfolioToRow = (
  commonRangeData: CommonRangeData,
  name: string,
  portfolio: Portfolio,
  rangeAnalysis: SubjectRangeAnalysis | undefined,
): BulkManagePortfolioRow => {
  const rowId = `bm${String(portfolio.id)}`;
  const commonFields = {
    rowType: 'investment' as const,
    investmentType: 'portfolio' as const,
    ...getCreateUpdateMetadata(portfolio),
    name,
    rowId,
    fullPath: [rowId],
    rangeLoading: false as const,
    commonRangeData,
    isBenchmark: true as const,
    secondary: true as const,
    // TODO(willw): can we remove frequency, start and end from this row?
    frequency: lowestFrequencyOf(flattenNode(portfolio).map((node) => node.fund?.returnFrequency)),
    startDate: portfolio.periodStart,
    endDate: portfolio.periodEnd,
  };
  if (portfolio.historical) {
    return {
      ...commonFields,
      allocationType: 'historical' as const,
      allocation: allocationTsToRecord(portfolio.closingAllocationsTs),
      startDate: rangeAnalysis?.start,
      endDate: rangeAnalysis?.end,
      historicalStartDate: rangeAnalysis?.historicalStart,
      historicalEndDate: rangeAnalysis?.historicalEnd,
      historicalError: getHistoricalError(rangeAnalysis?.historicalError),
    };
  }
  return {
    ...commonFields,
    allocationType: 'proforma' as const,
    allocation: portfolio.allocation,
  };
};

const mapFundToRow = (
  commonRangeData: CommonRangeData,
  name: string,
  strategyPath: number[],
  fund: FundToBulkProxy,
  fundProxyInfo: BulkPickerDetailedProxyMetadata | undefined,
  fundSecondProxyInfo: BulkPickerDetailedProxyMetadata | undefined,
  fundHoldingsProxyInfo: DetailedProxyMetadata | undefined,
  rangeAnalysis: SubjectRangeAnalysis | undefined,
  secondary: boolean,
  fundNode?: Portfolio,
): BulkManageFundRow => {
  const strategyStrPath = strategyPath.map(String);
  strategyStrPath.push(fund.id + (secondary ? 'bm' : ''));

  const commonFields = {
    rowType: 'investment' as const,
    investmentType: 'fund' as const,
    ...getCreateUpdateMetadata(fund),
    name,
    rowId: strategyStrPath.join('>'),
    fullPath: strategyStrPath,
    fundProxyInfo,
    fundSecondProxyInfo,
    fundHoldingsProxyInfo,
    investment: fund,
    dataSource: rebrandDataSource(fund.dataSource) ?? (fund.userUploaded ? 'Upload' : getAppTitle()),
    startDate: rangeAnalysis?.start,
    endDate: rangeAnalysis?.end,
    frequency: rangeAnalysis?.frequency,
    proxyStartDate: rangeAnalysis?.proxyStartDate,
    proxyEndDate: rangeAnalysis?.proxyEndDate,
    extrapolateStartDate: rangeAnalysis?.extrapolateStartDate,
    extrapolateEndDate: rangeAnalysis?.extrapolateEndDate,
    rangeLoading: isNil(rangeAnalysis),
    proxyInfos: rangeAnalysis?.proxyInfos,
    secondary,
    commonRangeData,
    isBenchmark: false,
  };

  if (fundNode?.historical) {
    return {
      ...commonFields,
      allocationType: 'historical',
      allocation: allocationTsToRecord(fundNode?.closingAllocationsTs),
      historicalStartDate: rangeAnalysis?.historicalStart,
      historicalEndDate: rangeAnalysis?.historicalEnd,
      historicalError: getHistoricalError(rangeAnalysis?.historicalError),
      // hacking rowId to workaround ag-grid bug where the table doesn't refresh
      // despite the row's `historicalError` field changing
      rowId: `${strategyStrPath.join('>')}-${rangeAnalysis?.historicalError}`,
    };
  }

  return {
    ...commonFields,
    allocationType: 'proforma',
    allocation: fundNode?.allocation,
  };
};

export const mapCashToRow = ({
  commonRangeData,
  rangeAnalysis,
  cashNode,
}: {
  commonRangeData: CommonRangeData;
  rangeAnalysis: SubjectRangeAnalysis | undefined;
  cashNode: WithStrategies<Portfolio>;
}): BulkManageCashRow => {
  const cashNodeId = String(cashNode.id);
  const fullPath = cashNode.strategyIdPath.map(String);
  fullPath.push(cashNodeId);

  return {
    rowType: 'investment' as const,
    investmentType: 'cash' as const,
    ...getCreateUpdateMetadata(cashNode),
    name: cashNode.name,
    rowId: cashNodeId,
    fullPath,
    startDate: rangeAnalysis?.start,
    endDate: rangeAnalysis?.end,
    frequency: rangeAnalysis?.frequency,
    rangeLoading: isNil(rangeAnalysis),
    commonRangeData,
    allocationType: 'proforma',
    allocation: cashNode.cashBalance,
  };
};

export const mapOptionToRow = ({
  commonRangeData,
  rangeAnalysis,
  optionNode,
}: {
  commonRangeData: CommonRangeData;
  rangeAnalysis: SubjectRangeAnalysis | undefined;
  optionNode: WithStrategies<Portfolio>;
}): BulkManageOptionRow => {
  const optionNodeId = String(optionNode.id);
  const fullPath = optionNode.strategyIdPath.map(String);
  fullPath.push(optionNodeId);

  return {
    rowType: 'investment' as const,
    investmentType: 'option' as const,
    ...getCreateUpdateMetadata(optionNode),
    name: optionNode.name,
    rowId: optionNodeId,
    fullPath,
    startDate: rangeAnalysis?.start,
    endDate: rangeAnalysis?.end,
    frequency: rangeAnalysis?.frequency,
    rangeLoading: isNil(rangeAnalysis),
    commonRangeData,
    allocationType: 'proforma',
    allocation: optionNode.allocation,
    symbol: optionNode.fund?.symbol,
    optionData: optionNode.fund?.optionData,
    dataSource: rebrandDataSource(optionNode.fund?.dataSource ?? optionNode.fund?.updatedByDataSource) ?? getAppTitle(),
  };
};

export const mapLotToRow = (
  commonRangeData: CommonRangeData,
  lot: Lot,
  lotIndex: number,
  parentFundRow: BulkManageFundRow,
  fund: FundToBulkProxy,
  totalLotQuantity: number,
): BulkManageLotRow => {
  const lotRowId = `${parentFundRow.rowId}>lot-${lotIndex}`;
  const fullPath = [...parentFundRow.fullPath, `lot-${lotIndex}`];

  const parentAllocation = parentFundRow.allocationType === 'proforma' ? (parentFundRow.allocation ?? 0) : 0;
  const pricePerShare = totalLotQuantity > 0 ? parentAllocation / totalLotQuantity : 0;
  const lotMarketValue = pricePerShare * (lot.quantity ?? 0);

  return {
    rowType: 'investment' as const,
    investmentType: 'lot' as const,
    name: `Lot ${lotIndex + 1}`,
    rowId: lotRowId,
    fullPath,
    investment: fund,
    dataSource: parentFundRow.dataSource,
    startDate: parentFundRow.startDate,
    endDate: parentFundRow.endDate,
    frequency: parentFundRow.frequency,
    rangeLoading: parentFundRow.rangeLoading,
    secondary: true,
    commonRangeData,
    allocationType: 'proforma',
    allocation: lotMarketValue,
    lot,
    quantity: lot.quantity,
    parentFundRowId: parentFundRow.rowId,
  };
};
